import classNames from "classnames/bind";
import { debounce } from "es-toolkit/compat";
import React, { useLayoutEffect, useRef, useState } from "react";
import { Icon, IconLoading, type IconProps } from "../../Icon";
import type { StepProps } from "../types";
import styles from "./Step.module.scss";

const cx = classNames.bind(styles);

const STEP_NUMBER_WIDTH = 24;
const STEP_LINE_WIDTH = 2;

const Step = ({
  label,
  description,
  intent = "default",
  stepNumber,
  state,
  isLast,
  orientation,
  onClick,
  listRef,
  isBusy = false,
  isFluid = false,
}: StepProps) => {
  const containerRef = useRef<HTMLLIElement>(null);
  const [gap, setGap] = useState(48);

  const handleStepNodeClick = () => {
    onClick?.(stepNumber);
  };

  const recalculateStepGap = (
    listEl: HTMLElement,
    orientation: StepProps["orientation"]
  ): number | undefined => {
    // Convert listRef children to iterable array
    const childNodes = [...(listEl.childNodes || [])];
    const position = stepNumber - 1;

    if (!childNodes.length || position >= childNodes.length - 1) return;

    const currentStepNumberBoundingRect = (
      childNodes[position].firstChild as Element
    ).getBoundingClientRect();

    const nextStepNumberBoundingRect = (
      childNodes[position + 1].firstChild as Element
    ).getBoundingClientRect();

    return orientation === "horizontal"
      ? nextStepNumberBoundingRect.left - currentStepNumberBoundingRect.right
      : nextStepNumberBoundingRect.top - currentStepNumberBoundingRect.bottom;
  };

  useLayoutEffect(
    /**
     * The Stepper component uses a <div> instead of a pseudoelement when
     * rendering the steps horizontally. We need to figure out the distance
     * between the current and the next step because the labels can be variable
     * length but we want the spacing between each step and line to be of equal
     * width. Part of that calculation requires knowing the gap between steps.
     * This gap is also intentionally calculated in an effect.
     */
    () => {
      if (!listRef) return;

      const recalculateGapAndAdjust = () => {
        const gap = recalculateStepGap(listRef, orientation);

        if (!gap) return;

        // for horizontal: The number of pixels between the right of the current stepNumber and the left of the next one
        // for vertical: The number of pixels between the bottom of the current stepNumber and the top of the next one
        setGap(gap);

        if (containerRef)
          containerRef.current?.style.setProperty(
            "--stepper-step-gap",
            `${gap}px`
          );
      };

      const ro = new ResizeObserver(debounce(recalculateGapAndAdjust, 100));

      // run it on container size changes
      ro.observe(listRef);
      // and run it immediately
      recalculateGapAndAdjust();

      return () => ro.disconnect();
    },
    [orientation, listRef, stepNumber, label, description, state, isBusy] // intentionally recalculated on non-referenced properties
  );

  const iconVariant: IconProps["variant"] = (() => {
    switch (intent) {
      case "error":
        return "exclamation-mark";
      case "warning":
        return "warning";
      case "pending":
      case "success":
      case "default":
        return "check";
    }
  })();

  const Element: keyof JSX.IntrinsicElements = onClick ? "button" : "div";

  return (
    <li
      className={cx("container", orientation, state, intent, { isLast })}
      aria-current={state === "current"}
      ref={containerRef}
    >
      <Element
        className={cx("stepNumber", state, intent)}
        style={{ width: STEP_NUMBER_WIDTH, height: STEP_NUMBER_WIDTH }}
        onClick={handleStepNodeClick}
      >
        {state === "future" ? stepNumber : null}
        {state === "complete" ? (
          <Icon
            variant={iconVariant}
            weight="bold"
            size={14}
            className={cx("icon")}
          />
        ) : null}
      </Element>

      <div className={cx("label-container")}>
        <p className={cx("label", { isFluid })}>
          {label}
          {isBusy ? (
            <IconLoading
              speed="slow"
              size={12}
              className={cx("icon", isBusy && "busy")}
            />
          ) : null}
        </p>
        {description ? (
          <p className={cx("description", { isFluid })}>{description}</p>
        ) : null}
      </div>

      {orientation === "horizontal" ? (
        <div
          className={cx("line", state)}
          style={{
            // The line should have 1/2 the width of a stepNumber as margin on either side
            width: `calc(${gap}px - ${STEP_NUMBER_WIDTH}px)`,
            borderTopWidth: STEP_LINE_WIDTH,
            top: STEP_NUMBER_WIDTH / 2 - STEP_LINE_WIDTH,
            /**
             * Since the line's width has 1/2 the width of a stepNumber for margin built in, setting the left value
             * to 50% + the width of the stepNumber will give the proper margin
             */
            left: `calc(50% + ${STEP_NUMBER_WIDTH}px)`,
          }}
        />
      ) : null}
    </li>
  );
};

export default Step;
